import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback
} from 'react'
import { CurrentUserContext } from '../Shared/CurrentUser.context'
import { CurrentPortalContext } from '../Shared/CurrentPortal.context'

import { getJWT } from '../../utils/functions'

import io from 'socket.io-client'
import { NotificationContext } from '../Shared/Notification.context'

export const ChatSocketContext = createContext(null)

export const ChatSocketContextProvider = ({ userId, portalId, ...props }) => {
  const [socket, setSocket] = useState(null)
  const [chats, setChats] = useState([])
  const [currChatIndex, setCurrChatIndex] = useState(-1)
  const [currChatName, setCurrChatName] = useState('')
  const [onlineUsers, setOnlineUsers] = useState([])
  const [portalUsers, setPortalUsers] = useState([])
  const [portalUsersMap, setPortalUsersMap] = useState({})
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [loadingChats, setLoadingChats] = useState(true)

  const { me } = useContext(CurrentUserContext)
  const { portal, fetchPortalUsers } = useContext(CurrentPortalContext)
  const { setSnackBarMessage } = useContext(NotificationContext)
  const [newMessage, setNewMessage] = useState(null)

  // Use ref to store socket instance for disconnect function
  const socketRef = useRef(null)

  useEffect(() => {
    const fetchParticipants = async () => {
      if (!portal?.name || !fetchPortalUsers) {
        return
      }
      try {
        const users = await fetchPortalUsers(portal.name)
        setPortalUsers(users.map((el) => el.user))

        const usersObj = users.reduce((acc, curr) => {
          acc[curr.user.id] = curr.user
          return acc
        }, {})

        setPortalUsersMap(usersObj)
      } catch (error) {
        console.error('Failed to fetch portal users:', error)
      }
    }

    fetchParticipants()
  }, [portalId, portal?.name, fetchPortalUsers])

  const onConnect = () => {
    setLoadingChats(false)
  }

  const onDisconnect = (data) => {
    // Disconnected
  }

  const processChats = (chats) => {
    const processedChats = chats.map((chat) => {
      const unreadMessages = chat.messages?.filter(
        (message) =>
          message.senderId !== me.id &&
          new Date(message.timestamp) > new Date(chat.lastSeen)
      ).length

      let lastMessageTime = null

      if (chat.messages) {
        lastMessageTime = chat.messages[chat.messages?.length - 1]?.timestamp
      }

      return {
        ...chat,
        lastMessageTime,
        unreadMessages
      }
    })

    return processedChats
  }

  const getChatName = (chat, currUserId) => {
    if (chat?.name) return chat.name
    const participant = chat?.participants?.find((p) => p.userId !== currUserId)
    if (participant && participant.userId in portalUsersMap) {
      return portalUsersMap[participant.userId]?.name
    }
    return ''
  }

  useEffect(() => {
    if (!newMessage) return
    if (
      newMessage.senderId !== me.id &&
      chats[currChatIndex]?.id !== newMessage.chatId
    ) {
      setSnackBarMessage(
        `New Chat Recieved\n${getChatName(newMessage.chat, me.id)}: ${
          newMessage.text
        }`,
        'info'
      )
    }
    setNewMessage(null)
  }, [newMessage, chats, currChatIndex, me])

  const onMessageSent = ({ message }) => {
    setNewMessage(message)

    setChats((prevChats) => {
      return processChats(
        prevChats.map((chat) => {
          if (chat.id === message.chatId) {
            return {
              ...chat,
              messages: chat.messages ? [...chat.messages, message] : [message]
            }
          } else {
            return chat
          }
        })
      )
    })
  }

  const onGetUserChats = ({ chats }) => {
    setChats(processChats(chats))
  }

  const onCreateChat = ({ chatWithUsers }) => {
    setChats((prev) => [...prev, chatWithUsers])
  }

  const onGetOnlineChatUsers = ({ users }) => {
    setOnlineUsers(users)
  }

  const onGetOnlinePortalUsers = ({ users }) => {
    setOnlineUsers(users.map((user) => user.userId))
  }

  const onUserConnected = ({ userId }) => {
    if (!onlineUsers.includes(userId)) {
      setOnlineUsers((prev) => [...prev, parseInt(userId)])
    }
  }

  const onUserDisconnected = ({ userId }) => {
    setOnlineUsers((prev) => prev.filter((id) => id !== parseInt(userId)))
  }

  const onUpdateLastSeen = ({ chatId, userId, lastSeen }) => {
    setChats((prevChats) => {
      return processChats(
        prevChats.map((chat) => {
          if (chat.id === chatId) {
            if (userId === me.id) {
              return {
                ...chat,
                lastSeen
              }
            } else {
              return {
                ...chat,
                otherUserLastSeen: lastSeen
              }
            }
          }
          return chat
        })
      )
    })
  }

  const onIsTyping = ({ chatId, senderId }) => {
    if (senderId !== me.id) {
      setChats((prevChats) => {
        return processChats(
          prevChats.map((chat) => {
            if (chat.id === chatId) {
              return {
                ...chat,
                isTypingDate: new Date()
              }
            }
            return chat
          })
        )
      })
    }
  }

  useEffect(() => {
    if (!userId || !portalId || !me) return

    let socketInstance = null

    const initializeSocket = async () => {
      try {
        socketInstance = io.connect(import.meta.env.VITE_CHATVU_BACKEND, {
          transports: ['websocket'],
          auth: {
            token: await getJWT()
          },
          query: {
            userId,
            portalId,
            userName: me.name,
            userEmail: me.email,
            userNumber: me.number,
            applicationId: 'televu-isee'
          }
        })

        setSocket(socketInstance)
        socketRef.current = socketInstance

        if (socketInstance && typeof socketInstance.on === 'function') {
          socketInstance.on('connect_error', (error) => {
            console.error('Connection error:', error)
          })
          socketInstance.on('connect', onConnect)
          socketInstance.on('disconnect', onDisconnect)
          socketInstance.on('chat:getUserChats', onGetUserChats)
          socketInstance.on('chat:sendMessage', onMessageSent)
          socketInstance.on('user:getOnlineChatUsers', onGetOnlineChatUsers)
          socketInstance.on('chat:updateLastSeen', onUpdateLastSeen)
          socketInstance.on('user:connected', onUserConnected)
          socketInstance.on('user:disconnected', onUserDisconnected)
          socketInstance.on('user:getOnlinePortalUsers', onGetOnlinePortalUsers)
          socketInstance.on('chat:chatCreated', onCreateChat)
          socketInstance.on('chat:isTyping', onIsTyping)
          if (typeof socketInstance.onAny === 'function') {
            socketInstance.onAny((eventName, ...args) => {
              // console.log('any', eventName, args)
            })
          }
        }
      } catch (error) {
        console.error('Failed to initialize socket:', error)
      }
    }

    initializeSocket()

    return () => {
      if (socketInstance && typeof socketInstance.off === 'function') {
        socketInstance.off('chat:getUserChats')
        socketInstance.off('chat:sendMessage')
        socketInstance.off('user:getOnlineChatUsers')
        socketInstance.off('chat:updateLastSeen')
        socketInstance.off('user:connected')
        socketInstance.off('user:disconnected')
        socketInstance.off('user:getOnlinePortalUsers')
        socketInstance.off('chat:chatCreated')
        socketInstance.off('chat:isTyping')
        if (typeof socketInstance.disconnect === 'function') {
          socketInstance.disconnect()
        }
      }
      socketRef.current = null
      setSocket(null)
    }
  }, [userId, portalId, me])

  // Function to disconnect the chat socket
  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      if (typeof socketRef.current.off === 'function') {
        socketRef.current.off('chat:getUserChats')
        socketRef.current.off('chat:sendMessage')
        socketRef.current.off('user:getOnlineChatUsers')
        socketRef.current.off('chat:updateLastSeen')
        socketRef.current.off('user:connected')
        socketRef.current.off('user:disconnected')
        socketRef.current.off('user:getOnlinePortalUsers')
        socketRef.current.off('chat:chatCreated')
        socketRef.current.off('chat:isTyping')
        socketRef.current.off('connect')
        socketRef.current.off('disconnect')
        socketRef.current.off('connect_error')
      }
      if (typeof socketRef.current.disconnect === 'function') {
        socketRef.current.disconnect()
      }
      socketRef.current = null
      setSocket(null)
    }
  }, [])

  return (
    <ChatSocketContext.Provider
      value={{
        socket,
        chats,
        currChatIndex,
        setCurrChatIndex,
        currChatName,
        setCurrChatName,
        onlineUsers,
        setOnlineUsers,
        portalUsers,
        setPortalUsers,
        portalUsersMap,
        setPortalUsersMap,
        isSettingsOpen,
        setIsSettingsOpen,
        loadingChats,
        setLoadingChats,
        disconnectSocket
      }}
    >
      {props.children}
    </ChatSocketContext.Provider>
  )
}
